import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import GuideLead from '../components/GuideLead';

const GuideMotsClesCV = () => {
    return (
        <div className="min-h-screen bg-slate-200">
            <Helmet>
                <title>Mots-clés CV : Liste par Secteur et Métier 2025 - Guide Complet</title>
                <meta name="description" content="Liste complète de mots-clés CV par secteur et métier : IT, commerce, marketing, RH, finance. Verbes d'action, mots-clés ATS, comment les intégrer naturellement." />
                <meta name="keywords" content="mots-clés CV, mots-clés CV par métier, vocabulaire CV, verbes d'action CV, mots-clés ATS CV, optimiser mots-clés CV" />
                <link rel="canonical" href="https://www.cv-analyse.fr/guide/mots-cles-cv" />
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  "name": "Comment intégrer les bons mots-clés dans son CV",
                  "description": "Optimisez votre CV avec les mots-clés adaptés à votre secteur pour passer les filtres ATS et attirer les recruteurs.",
                  "step": [
                    {"@type": "HowToStep", "name": "Analyser l'offre d'emploi", "text": "Relevez les termes techniques et compétences mentionnés dans l'annonce."},
                    {"@type": "HowToStep", "name": "Identifier les mots-clés par secteur", "text": "Consultez la liste des mots-clés courants pour votre métier et votre secteur."},
                    {"@type": "HowToStep", "name": "Intégrer les mots-clés naturellement", "text": "Incorporez-les dans vos expériences et compétences sans sur-optimiser."},
                    {"@type": "HowToStep", "name": "Utiliser des verbes d'action percutants", "text": "Commencez vos bullet points par des verbes forts : géré, développé, optimisé, dirigé."},
                    {"@type": "HowToStep", "name": "Vérifier le score ATS", "text": "Utilisez CV Analyse pour mesurer la densité et la pertinence de vos mots-clés."}
                  ]
                }
                `}</script>
            </Helmet>

            <nav aria-label="Fil d'Ariane" className="pt-16 pb-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ol className="flex items-center space-x-2 text-sm text-slate-600">
                        <li><Link to="/" className="hover:text-white transition-colors">Accueil</Link></li>
                        <li className="text-slate-400">/</li>
                        <li><Link to="/guides" className="hover:text-white transition-colors">Guides</Link></li>
                        <li className="text-slate-400">/</li>
                        <li className="text-white">Mots-clés CV</li>
                    </ol>
                </div>
            </nav>

            <div className="bg-slate-900 pt-4 pb-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-3xl mb-6">
                            <span className="text-4xl">🔑</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                            Mots-clés CV : Liste par Secteur et Métier 2025
                        </h1>
                        <p className="text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed font-medium">
                            Découvrez les mots-clés essentiels pour optimiser votre CV selon votre secteur d'activité
                        </p>
                    </div>
                </div>
            </div>

            <GuideLead slug="mots-cles-cv" />

            <article className="px-6 py-16 mx-auto max-w-5xl">
                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">💪 Verbes d'action puissants</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {['Diriger', 'Optimiser', 'Développer', 'Gérer', 'Créer', 'Améliorer', 'Augmenter', 'Réduire', 'Implémenter', 'Coordonner', 'Analyser', 'Concevoir', 'Superviser', 'Former', 'Négocier', 'Stratégiser'].map((verbe) => (
                            <div key={verbe} className="bg-teal-50 rounded-xl p-3 text-center font-semibold text-gray-800">
                                {verbe}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="space-y-8 mb-8">
                    {[
                        { secteur: 'IT / Tech', mots: ['JavaScript', 'Python', 'React', 'Node.js', 'DevOps', 'Cloud', 'Agile', 'Scrum', 'API', 'Git', 'Docker', 'Kubernetes'] },
                        { secteur: 'Commerce / Vente', mots: ['CA', 'Objectifs', 'Prospection', 'Négociation', 'CRM', 'Fidélisation', 'B2B', 'B2C', 'Pipeline', 'ROI'] },
                        { secteur: 'Marketing Digital', mots: ['SEO', 'SEM', 'Google Analytics', 'Social Media', 'Content Marketing', 'Email Marketing', 'ROAS', 'Conversion', 'A/B Testing'] },
                        { secteur: 'RH', mots: ['Recrutement', 'Talent Acquisition', 'Onboarding', 'GPEC', 'Formation', 'SIRH', 'Employer Branding', 'Développement RH'] },
                        { secteur: 'Finance', mots: ['Comptabilité', 'Audit', 'Contrôle de gestion', 'Reporting', 'Budget', 'Trésorerie', 'IFRS', 'Tableaux de bord'] }
                    ].map((item) => (
                        <div key={item.secteur} className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">{item.secteur}</h2>
                            <div className="flex flex-wrap gap-3">
                                {item.mots.map((mot) => (
                                    <span key={mot} className="bg-teal-100 text-teal-800 px-4 py-2 rounded-full text-sm font-semibold">
                                        {mot}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-6">🎯 Comment intégrer les mots-clés naturellement</h2>
                    <ul className="space-y-4 text-gray-700">
                        <li className="flex items-start"><span className="text-teal-600 mr-3">1.</span>Lisez attentivement l'offre d'emploi et repérez les mots-clés récurrents</li>
                        <li className="flex items-start"><span className="text-teal-600 mr-3">2.</span>Intégrez-les dans votre profil professionnel</li>
                        <li className="flex items-start"><span className="text-teal-600 mr-3">3.</span>Utilisez-les dans les descriptions d'expériences</li>
                        <li className="flex items-start"><span className="text-teal-600 mr-3">4.</span>Créez une section "Compétences" avec les mots-clés pertinents</li>
                        <li className="flex items-start"><span className="text-teal-600 mr-3">5.</span>Évitez le "keyword stuffing" - intégrez-les naturellement</li>
                    </ul>
                </div>

                <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-3xl p-8 text-center text-white mb-16">
                    <h2 className="text-3xl font-bold mb-4">Votre CV contient-il les bons mots-clés pour être sélectionné ?</h2>
                    <p className="text-xl mb-6">Notre IA identifie les mots-clés manquants et optimise votre CV pour chaque offre — score et 3 conseils offerts</p>
                    <Link to="/" className="inline-block bg-white text-teal-600 font-bold px-8 py-4 rounded-2xl hover:bg-gray-100 transition-colors">
                        Analyser mes mots-clés gratuitement
                    </Link>
                </div>
            </article>
        </div>
    );
};

export default GuideMotsClesCV;








